import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, Calendar, CheckCircle2, ChevronDown, ChevronLeft, ExternalLink, Eye, FileOutput, Info, Package, Send, ToggleLeft, ToggleRight, Users, X, XCircle } from 'lucide-react';
import { useUserProfile } from '../../context/UserProfileContext';
import { useSharedQuote } from '../../context/QuoteContext';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import { useOrgRates } from '../../hooks/useOrgRates';
import { useResidentialFlatRates } from '../../hooks/useResidentialFlatRates';
import { useSmartMovingIntegration } from '../../hooks/useSmartMovingIntegration';
import { useVersionExportPacket } from '../../hooks/useVersionExportPacket';
import { computeQuoteMaterials } from '../../lib/inventoryPricing';
import { api } from '../../lib/api';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { SuccessToast } from '../../components/ui/SuccessToast';
import { TaxBreakdown } from '../../components/ui/TaxBreakdown';
import { CopySectionButton, ExportVersionSheet, EXPORT_COPY_LABELS } from '../../components/quotes/ExportVersionSheet';
import type { ExportCopyKey } from '../../components/quotes/ExportVersionSheet';
import {
  currencySymbol,
  formatMoveDate,
  QuoteVersion,
  getVersionExpirationStatus,
  formatExpiration,
} from '../../types/admin';
import type { ItemizationPreference } from '../../types/admin';
import { buildCrewFromFormula, buildCrewFromOrgRates } from '../../utils/formulaCalculation';
import { checkFormulaReadiness } from '../../utils/formulaReadiness';
import { RateCardHint } from '../../components/ui/RateCardHint';
import { resolveOrgValidityDays } from '@iomi/shared';

// ── Helpers ─────────────────────────────────────────────────────────────────

function fmtCurrency(n: number, sym: string): string {
  return `${sym}${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtQty(n: number): string {
  return n % 1 === 0 ? String(n) : n.toFixed(1);
}

/**
 * Returns true when the live quote data differs from the latest saved version,
 * meaning the user has unsaved changes worth showing in "Latest (unsaved)".
 */
function hasUnsavedChanges(
  quote: { customerCompanyName: string; addresses?: { origin?: { address?: string }; destination?: { address?: string } }; pointOfContact?: { email?: string }; movingDate?: { preferredDate?: string; deadline?: string }; formula?: { finalCostEstimate: number } | null },
  latest: QuoteVersion,
): boolean {
  if (quote.customerCompanyName !== latest.customerCompanyName) return true;
  if ((quote.addresses?.origin?.address ?? '') !== (latest.originAddress ?? '')) return true;
  if ((quote.addresses?.destination?.address ?? '') !== (latest.destinationAddress ?? '')) return true;
  if ((quote.pointOfContact?.email ?? '') !== (latest.pointOfContactEmail ?? '')) return true;
  if ((quote.movingDate?.preferredDate ?? '') !== (latest.preferredMoveDate ?? '')) return true;
  if ((quote.movingDate?.deadline ?? '') !== (latest.moveDeadline ?? '')) return true;
  if ((quote.formula?.finalCostEstimate ?? 0) !== latest.totalQuotedCost) return true;
  return false;
}

// ── Version Dropdown ────────────────────────────────────────────────────────

function VersionDropdown({
  versions,
  selectedId,
  onSelect,
  showUnsaved,
}: {
  versions: QuoteVersion[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  showUnsaved: boolean;
}) {
  const [open, setOpen] = useState(false);
  const selected = selectedId ? versions.find(v => v.id === selectedId) : null;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-stone bg-white
                   text-[13px] font-medium text-midnight hover:border-plum/40
                   transition-colors min-w-[140px]"
      >
        <span className="flex-1 text-left truncate">
          {selected ? `v${selected.versionNumber} — ${selected.quoteDate}` : 'Latest (unsaved)'}
        </span>
        <ChevronDown size={14} className={`shrink-0 text-slate transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <ul className="absolute left-0 top-full mt-1 z-50 w-56 bg-white border border-stone rounded-xl
                         shadow-brand-md overflow-hidden">
            {showUnsaved && (
              <li>
                <button
                  type="button"
                  onClick={() => { onSelect(null); setOpen(false); }}
                  className={`w-full text-left px-3 py-2.5 text-[13px] hover:bg-mist transition-colors
                    ${!selectedId ? 'font-semibold text-plum bg-plum/5' : 'text-midnight'}`}
                >
                  Latest (unsaved)
                </button>
              </li>
            )}
            {versions.map((v, i) => {
              const expStatus = getVersionExpirationStatus(v);
              return (
                <li key={v.id}>
                  <button
                    type="button"
                    onClick={() => { onSelect(v.id); setOpen(false); }}
                    className={`w-full text-left px-3 py-2.5 text-[13px] hover:bg-mist transition-colors
                      ${(showUnsaved || i > 0) ? 'border-t border-stone' : ''}
                      ${selectedId === v.id ? 'font-semibold text-plum bg-plum/5' : 'text-midnight'}`}
                  >
                    <span>v{v.versionNumber} &mdash; {v.quoteDate}</span>
                    {expStatus === 'expired' && (
                      <span className="ml-2 text-[11px] font-medium text-rose">Expired</span>
                    )}
                    {expStatus === 'expiring_soon' && (
                      <span className="ml-2 text-[11px] font-medium text-amber-600">Expiring soon</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}

// ── Compact date input (matches DateInput from DatesLogisticsPage) ─────────

function ExpirationDateInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  const formatted = value ? (() => {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  })() : null;

  return (
    <div className="flex items-center gap-2">
      <span className="text-[13px] font-medium text-slate whitespace-nowrap">Expires</span>
      <div
        className="relative h-9 rounded-lg border border-stone bg-white flex items-center px-3 gap-2
                   focus-within:border-plum focus-within:ring-2 focus-within:ring-plum/20
                   transition-colors duration-150 min-w-[140px]"
      >
        <input
          ref={inputRef}
          type="date"
          value={value}
          onChange={e => onChange(e.target.value)}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
        />
        <span className={`flex-1 text-[13px] ${formatted ? 'text-midnight font-medium' : 'text-slate italic'}`}>
          {formatted ?? 'No expiration'}
        </span>
        <Calendar size={14} className="text-slate shrink-0" />
      </div>
    </div>
  );
}

/** Compute default expiration date string (YYYY-MM-DD) from org validity days. */
function computeDefaultExpiration(validityDays: number | null | undefined): string {
  if (validityDays === null || validityDays === undefined || validityDays <= 0) return '';
  const d = new Date();
  d.setDate(d.getDate() + validityDays);
  return d.toISOString().split('T')[0];
}

// ── Read-only detail row ────────────────────────────────────────────────────

function DetailRow({ label, value }: { label: string; value: string | undefined }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5 border-b border-stone last:border-0">
      <p className="text-[13px] text-slate">{label}</p>
      <p className="text-[13px] font-medium text-midnight text-right">{value || '—'}</p>
    </div>
  );
}

// ── Line item row ───────────────────────────────────────────────────────────

function LineItemRow({
  name,
  rate,
  qty,
  total,
  sym,
  kind,
}: {
  name: string;
  rate: number;
  qty: number;
  total: number;
  sym: string;
  kind?: 'materials';
}) {
  return (
    <div className="py-2.5 border-b border-stone last:border-0">
      <p className="text-[13px] font-medium text-midnight mb-1">{name}</p>
      <div className="flex items-center justify-between text-[12px] text-slate">
        {kind === 'materials'
          ? <span>{fmtQty(qty)} item{qty !== 1 ? 's' : ''} · flat amount</span>
          : <span>{fmtCurrency(rate, sym)}/hr &times; {fmtQty(qty)} hrs</span>}
        <span className="font-semibold text-midnight">{fmtCurrency(total, sym)}</span>
      </div>
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

export function ClientQuotesPage() {
  const { quoteId } = useParams<{ quoteId: string }>();
  const navigate = useNavigate();
  const { activeOrg } = useUserProfile();
  const orgId = activeOrg?.orgId ?? null;
  const { quote, loading, updateQuote } = useSharedQuote();
  const { org } = useOrgSettings(orgId ?? '');
  const { orgItems } = useSharedQuote();
  const { rates: orgRates } = useOrgRates(orgId ?? '');
  const { flatRates: orgFlatRates, loading: orgFlatRatesLoading } = useResidentialFlatRates(orgId ?? '');
  const isOrgAdmin = activeOrg?.role === 'admin' || activeOrg?.role === 'field_lead';
  const basePath = `/moves/${quoteId}`;

  const [versions, setVersions] = useState<QuoteVersion[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(true);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [showMissingWarning, setShowMissingWarning] = useState(false);
  const [showExpireConfirm, setShowExpireConfirm] = useState(false);
  const [initialSelectionDone, setInitialSelectionDone] = useState(false);
  const [expirationDate, setExpirationDate] = useState('');
  const [expirationInitDone, setExpirationInitDone] = useState(false);
  const symbol = currencySymbol(org?.defaultCurrency);

  // Export: which flow the expire-previous confirmation continues into, the
  // export sheet, and the "Copied" toast. The packet is prefetched for the
  // selected version so copy buttons write to the clipboard inside the tap.
  type VersionAction = 'pdf' | 'export';
  const [pendingAction, setPendingAction] = useState<VersionAction>('pdf');
  const [exportOpen, setExportOpen] = useState(false);
  const [copiedKey, setCopiedKey] = useState<ExportCopyKey | null>(null);
  const {
    packet: exportPacket,
    loading: exportLoading,
    error: exportError,
  } = useVersionExportPacket(orgId, quoteId, selectedVersionId);

  // SmartMoving push-back state
  const { config: smConfig, pushQuote } = useSmartMovingIntegration(orgId ?? '');
  const canPushToSM = smConfig?.enabled && smConfig?.pushBackEnabled && !!smConfig?.providerKey;
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState<{ success: boolean; message: string } | null>(null);

  // Initialize expiration date from org settings once loaded
  useEffect(() => {
    if (expirationInitDone || !org) return;
    setExpirationInitDone(true);
    setExpirationDate(computeDefaultExpiration(resolveOrgValidityDays(org, quote?.type)));
  }, [org, expirationInitDone, quote?.type]);

  // Fetch versions on mount
  useEffect(() => {
    if (!orgId || !quoteId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<{ success: boolean; data: QuoteVersion[] }>(
          `/quotes/${orgId}/${quoteId}/versions`,
        );
        if (!cancelled) setVersions(res.data);
      } catch (err) {
        console.error('[listVersions]', err);
      } finally {
        if (!cancelled) setVersionsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, quoteId]);

  // Smart initial selection: if the latest version matches current quote data, pre-select it
  useEffect(() => {
    if (initialSelectionDone || versionsLoading || loading || !quote) return;
    setInitialSelectionDone(true);
    if (versions.length > 0) {
      const latest = versions[0];
      if (!hasUnsavedChanges(quote, latest)) {
        setSelectedVersionId(latest.id);
      }
    }
  }, [versions, versionsLoading, loading, quote, initialSelectionDone]);

  // Formula readiness with the same org settings the engine prices with — a $0
  // crew/truck rate (or a $0 / disabled flat-rate tier) must block generation
  // even when a stale formula is still stored on the quote.
  const readiness = checkFormulaReadiness(quote, {
    effectiveCrew: quote?.crew ?? buildCrewFromOrgRates(orgRates),
    useBlendedTruckDriverRate: org?.useBlendedTruckDriverRate,
    residentialPricingModel: org?.residentialPricingModel,
    flatRates: orgFlatRatesLoading ? undefined : orgFlatRates,
  });

  // Compute missing details required for generating a client quote
  function getMissingDetails(): string[] {
    if (!quote) return ['Quote data'];
    const missing: string[] = [];
    if (!quote.formula) missing.push('Formula calculation');
    if (readiness.missing.includes('rate card')) missing.push('Rate card');
    if (!quote.addresses?.origin?.address) missing.push('Origin address');
    if (!quote.addresses?.internalMove && !quote.addresses?.destination?.address) missing.push('Destination address');
    if (!quote.pointOfContact?.email) missing.push('Point of contact email');
    if (!quote.movingDate?.preferredDate) missing.push('Preferred move date');
    return missing;
  }

  const missingDetails = getMissingDetails();
  const canGenerate = missingDetails.length === 0;

  // Check if there are non-expired previous versions
  function hasActiveVersions(): boolean {
    return versions.some(v => {
      const status = getVersionExpirationStatus(v);
      return status !== 'expired';
    });
  }

  /**
   * Both "Generate Client Quote" (PDF) and "Export" create a version from the
   * draft; they share the readiness gate and the expire-previous confirmation.
   * Export skips the PDF (it can be generated later from the version page) and
   * opens the export sheet on the new version instead of navigating away.
   */
  function handleGenerateClick(action: VersionAction = 'pdf') {
    if (!canGenerate) {
      setShowMissingWarning(true);
      return;
    }
    setPendingAction(action);
    // If there are active (non-expired) previous versions, ask about expiring them
    if (hasActiveVersions()) {
      setShowExpireConfirm(true);
      return;
    }
    handleGenerate(false, action);
  }

  async function handleGenerate(expirePrevious: boolean, action: VersionAction = pendingAction) {
    if (!orgId || !quoteId) return;
    setGenerating(true);
    setShowExpireConfirm(false);
    try {
      // Optionally expire all non-expired previous versions
      if (expirePrevious) {
        await api.post(`/quotes/${orgId}/${quoteId}/versions/expire-previous`, {});
        // Update local state: set all existing versions to expired
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        yesterday.setHours(23, 59, 59, 999);
        const expiredAt = yesterday.toISOString();
        setVersions(prev => prev.map(v => {
          const status = getVersionExpirationStatus(v);
          return status !== 'expired' ? { ...v, expiresAt: expiredAt } : v;
        }));
      }

      const res = await api.post<{ success: boolean; data: QuoteVersion }>(
        `/quotes/${orgId}/${quoteId}/versions`,
        {
          ...(expirationDate ? { quoteExpirationDate: expirationDate } : {}),
          ...(action === 'export' ? { skipPdf: true } : {}),
        },
      );
      const newVersion = res.data;
      setVersions(prev => [newVersion, ...prev]);
      setSelectedVersionId(newVersion.id);
      setShowMissingWarning(false);
      // Backend already persisted the status change; update local state to reflect it
      updateQuote({ quoteStatus: 'sent' }).catch(() => {/* already saved server-side */});
      if (action === 'export') {
        // Stay here: the export sheet opens on the new version.
        setExportOpen(true);
      } else {
        // Navigate to the version view page
        navigate(`${basePath}/client-quotes/${newVersion.id}`);
      }
    } catch (err) {
      console.error('[createVersion]', err);
    } finally {
      setGenerating(false);
    }
  }

  async function handlePushToSmartMoving() {
    if (!orgId || !quoteId) return;
    setPushing(true);
    setPushResult(null);
    try {
      const result = await pushQuote(quoteId);
      setPushResult({ success: true, message: result.message });
    } catch (err) {
      setPushResult({
        success: false,
        message: err instanceof Error ? err.message : 'Push failed',
      });
    } finally {
      setPushing(false);
    }
  }

  if (loading || versionsLoading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="px-4 pt-6 flex flex-col items-center py-20 gap-2 text-center">
        <p className="text-small text-rose font-semibold">Quote not found</p>
      </div>
    );
  }

  const f = quote.formula;

  // Determine what to show: selected version data, or live quote data
  const selectedVersion = selectedVersionId
    ? versions.find(v => v.id === selectedVersionId) ?? null
    : null;
  const latestVersion = versions.length > 0 ? versions[0] : null;
  const isViewingOldVersion = selectedVersion && latestVersion && selectedVersion.id !== latestVersion.id;

  // Should we show the "Latest (unsaved)" option in the dropdown?
  const showUnsaved = !latestVersion || hasUnsavedChanges(quote, latestVersion);

  // Build display data — either from a saved version or from the live quote
  const isInternalMove = !!quote.addresses?.internalMove;
  const travelTimeRateSetting = quote.travelTimeRate === 'portal_to_portal'
    ? 'Portal to Portal'
    : '1 Hour Standard';

  // Determine itemization preference based on quote type and org settings
  const quoteType = quote.type ?? 'commercial';
  const itemizationPref: ItemizationPreference = quoteType === 'commercial'
    ? (org?.commercialItemizationPreference ?? 'single_line')
    : (org?.residentialItemizationPreference ?? 'single_line');

  // Materials & Scope of Services enablement
  const materialsEnabled = quoteType === 'commercial'
    ? org?.commercialItemizeMaterials === true
    : org?.residentialItemizeMaterials === true;
  const servicesEnabled = quoteType === 'commercial'
    ? org?.commercialItemizeScopeOfServices === true
    : org?.residentialItemizeScopeOfServices === true;

  // Materials count: the client-listed quantity (packing items + priced equipment),
  // the one number used on the charge line, in Additional Details, on the version
  // and on the portal. Computed live so a stale stored formula can't disagree.
  const listedMaterialsCount = computeQuoteMaterials(quote, orgItems).listedCount;
  const liveMaterialsCount = materialsEnabled ? listedMaterialsCount : 0;

  // Live scope of services count (enabled services)
  const liveServicesCount = servicesEnabled
    ? (quote.scopeOfServices ?? []).filter(s => s.enabled).length
    : 0;

  // Live line items (built from current formula and itemization preference).
  // The labor lines split the labor portion; materials are one flat line on top.
  type LiveLineItem = { name: string; rate: number; qty: number; total: number; kind?: 'materials' };
  function buildLiveLineItems(): LiveLineItem[] {
    if (!f) return [];
    const materialsCost = f.materialsCost ?? 0;
    const items: LiveLineItem[] = buildLaborLineItems();
    if (materialsCost > 0) {
      items.push({ name: `Materials (\u00d7${listedMaterialsCount})`, rate: 0, qty: listedMaterialsCount, total: materialsCost, kind: 'materials' });
    }
    return items;
  }

  function buildLaborLineItems(): LiveLineItem[] {
    if (!f) return [];
    const laborCost = f.finalCostEstimate - (f.materialsCost ?? 0);
    const totalHours = f.actualJobDuration + f.travelTimeToJobHours + (isInternalMove ? 0 : f.finalTransitTimeHours);

    if (itemizationPref === 'crew_trucks') {
      const crewMembers = buildCrewFromFormula(f, quote!.crew ?? [], quoteType, org?.useBlendedTruckDriverRate);
      return crewMembers.map(m => ({
        name: `${m.name} (\u00d7${m.quantity})`,
        rate: m.hourlyRate * m.quantity,
        qty: totalHours,
        total: m.hourlyRate * m.quantity * totalHours,
      }));
    }

    if (itemizationPref === 'duration_transit') {
      return [
        {
          name: 'Move Inventory',
          rate: f.totalHourlyRate,
          qty: f.actualJobDuration,
          total: f.totalHourlyRate * f.actualJobDuration,
        },
        {
          name: `Crew Travel Time (${travelTimeRateSetting})`,
          rate: f.totalHourlyRate,
          qty: f.travelTimeToJobHours,
          total: f.totalHourlyRate * f.travelTimeToJobHours,
        },
        ...(!isInternalMove ? [{
          name: `Transit Time above 1hr (est: ${fmtQty(f.finalTransitTimeHours > 0 ? f.finalTransitTimeHours + 1 : 0)} hours)`,
          rate: f.totalHourlyRate,
          qty: f.finalTransitTimeHours,
          total: f.totalHourlyRate * f.finalTransitTimeHours,
        }] : []),
      ];
    }

    if (itemizationPref === 'flat_rate_addons') {
      const baseRate = f.flatRateRoomPrice ?? laborCost;
      const items: LiveLineItem[] = [{
        name: 'Standard Moving Service',
        rate: baseRate,
        qty: 1,
        total: baseRate,
      }];
      const originProp = quote!.addresses?.origin?.residentialProperty;
      const destProp = quote!.addresses?.destination?.residentialProperty;
      if (originProp?.longCarry || destProp?.longCarry) {
        items.push({ name: 'Long Carry', rate: 0, qty: 1, total: 0 });
      }
      if (originProp?.elevator || destProp?.elevator) {
        items.push({ name: 'Elevator Access', rate: 0, qty: 1, total: 0 });
      }
      const originStories = originProp?.stories ?? 0;
      const destStories = destProp?.stories ?? 0;
      if (originStories > 1 || destStories > 1) {
        const maxStories = Math.max(originStories, destStories);
        items.push({ name: `Flights of Stairs (${maxStories} stories)`, rate: 0, qty: 1, total: 0 });
      }
      return items;
    }

    // single_line for residential flat rate
    if (quoteType === 'residential' && f.isFlatRate) {
      return [{
        name: 'Standard Moving Service',
        rate: laborCost,
        qty: 1,
        total: laborCost,
      }];
    }

    // single_line: one combined charge
    return [{
      name: 'Moving Services',
      rate: f.totalHourlyRate,
      qty: totalHours,
      total: laborCost,
    }];
  }

  const liveLineItems = buildLiveLineItems();

  // Resolve billing info for display (apply "same as" logic)
  const billing = quote.billingInfo;
  const resolvedBillingName = billing?.sameAsPointOfContact
    ? (quote.pointOfContact?.name ?? '')
    : (billing?.name ?? '');
  const resolvedBillingAddr = billing?.sameAsOriginAddress
    ? { line1: quote.addresses?.origin?.address ?? '', line2: '', city: '', state: '', zip: '' }
    : {
        line1: billing?.addressLine1 ?? '',
        line2: billing?.addressLine2 ?? '',
        city: billing?.city ?? '',
        state: billing?.state ?? '',
        zip: billing?.zipCode ?? '',
      };

  const isResidential = quote.type === 'residential';

  const displayData = selectedVersion
    ? {
        quoteVersionId: selectedVersion.quoteVersionId,
        quoteDate: selectedVersion.quoteDate,
        customerName: selectedVersion.customerCompanyName,
        originAddress: selectedVersion.originAddress,
        destinationAddress: selectedVersion.destinationAddress,
        customerEmail: selectedVersion.pointOfContactEmail,
        packingDate: selectedVersion.packingDate,
        preferredMoveDate: selectedVersion.preferredMoveDate,
        moveDeadline: selectedVersion.moveDeadline,
        lineItems: selectedVersion.lineItems,
        totalQuotedCost: selectedVersion.totalQuotedCost,
        materialsCount: selectedVersion.materials?.reduce((sum, m) => sum + m.quantity, 0) ?? 0,
        servicesCount: selectedVersion.scopeOfServices?.length ?? 0,
        showMaterials: (selectedVersion.materials?.length ?? 0) > 0,
        showServices: (selectedVersion.scopeOfServices?.length ?? 0) > 0,
        billingName: selectedVersion.billingName ?? '',
        billingAddressLine1: selectedVersion.billingAddressLine1 ?? '',
        billingAddressLine2: selectedVersion.billingAddressLine2 ?? '',
        billingCity: selectedVersion.billingCity ?? '',
        billingState: selectedVersion.billingState ?? '',
        billingZipCode: selectedVersion.billingZipCode ?? '',
        // Tax data from version snapshot
        taxState: selectedVersion.taxState,
        laborTaxRate: selectedVersion.laborTaxRate,
        materialsTaxRate: selectedVersion.materialsTaxRate,
        materialsCost: selectedVersion.lineItems.find(li => li.kind === 'materials')?.total ?? 0,
        taxAmount: selectedVersion.taxAmount,
        totalWithTax: selectedVersion.totalWithTax,
        requiresManualTaxReview: selectedVersion.requiresManualTaxReview,
      }
    : {
        quoteVersionId: quote.quoteNumber
          ? `${quote.quoteNumber}-${versions.length + 1}`
          : '—',
        quoteDate: new Date().toISOString().split('T')[0],
        customerName: isResidential
          ? (quote.pointOfContact?.name ?? quote.customerCompanyName)
          : quote.customerCompanyName,
        originAddress: quote.addresses?.origin?.address,
        destinationAddress: isInternalMove ? 'Internal move' : quote.addresses?.destination?.address,
        customerEmail: quote.pointOfContact?.email,
        packingDate: quote.packingDate ?? undefined,
        preferredMoveDate: quote.movingDate?.preferredDate,
        moveDeadline: quote.movingDate?.deadline,
        lineItems: liveLineItems,
        totalQuotedCost: f?.finalCostEstimate ?? 0,
        materialsCount: liveMaterialsCount,
        servicesCount: liveServicesCount,
        showMaterials: materialsEnabled,
        showServices: servicesEnabled,
        billingName: resolvedBillingName,
        billingAddressLine1: resolvedBillingAddr.line1,
        billingAddressLine2: resolvedBillingAddr.line2,
        billingCity: resolvedBillingAddr.city,
        billingState: resolvedBillingAddr.state,
        billingZipCode: resolvedBillingAddr.zip,
        // Tax data from live quote
        taxState: quote.taxState,
        laborTaxRate: quote.laborTaxRate,
        materialsTaxRate: quote.materialsTaxRate,
        materialsCost: f?.materialsCost ?? 0,
        taxAmount: quote.taxAmount,
        totalWithTax: quote.taxAmount != null && quote.taxAmount > 0
          ? (f?.finalCostEstimate ?? 0) + quote.taxAmount
          : undefined,
        requiresManualTaxReview: quote.requiresManualTaxReview,
      };

  return (
    <div className="px-4 pt-6 pb-10 md:mx-auto md:max-w-3xl md:px-6">
      {/* Back */}
      <button
        type="button"
        onClick={() => navigate(basePath)}
        className="flex items-center gap-1 text-small text-slate hover:text-midnight mb-4
                   transition-colors -ml-1"
      >
        <ChevronLeft size={16} />
        {quote.customerCompanyName}
      </button>

      {/* Title row with version dropdown */}
      <div className="flex items-center justify-between gap-3 mb-5">
        <h1 className="font-display text-h1 text-midnight">Client Quotes</h1>
        <VersionDropdown
          versions={versions}
          selectedId={selectedVersionId}
          onSelect={setSelectedVersionId}
          showUnsaved={showUnsaved}
        />
      </div>

      {/* Customer Quote Details container */}
      <div className="flex flex-col gap-4">

        {/* Callout */}
        <div className="flex gap-2.5 p-3 bg-plum/5 border border-plum/20 rounded-xl">
          <Info size={16} className="shrink-0 text-plum mt-0.5" />
          <p className="text-[12px] text-slate leading-snug">
            The information shown on this page is the information that will appear on the prepared
            quote PDF sent to the client.
          </p>
        </div>

        {/* Expiration date + action button row */}
        <div className="flex items-center justify-between gap-3">
          {!selectedVersion ? (
            <ExpirationDateInput
              value={expirationDate}
              onChange={setExpirationDate}
            />
          ) : selectedVersion && getVersionExpirationStatus(selectedVersion) !== 'expired' ? (
            <ExpirationDateInput
              value={selectedVersion.expiresAt ? selectedVersion.expiresAt.split('T')[0] : ''}
              onChange={async (newDate) => {
                if (!orgId || !quoteId) return;
                try {
                  const res = await api.patch<{ success: boolean; data: QuoteVersion }>(
                    `/quotes/${orgId}/${quoteId}/versions/${selectedVersion.id}`,
                    { quoteExpirationDate: newDate },
                  );
                  setVersions(prev => prev.map(v => v.id === selectedVersion.id ? res.data : v));
                } catch (err) {
                  console.error('[updateVersionExpiration]', err);
                }
              }}
            />
          ) : <div />}
          <div className="flex items-center gap-3 flex-wrap justify-end">
            {/* Export — saved versions open the export sheet; drafts create a version (no PDF) first */}
            <button
              type="button"
              onClick={() => selectedVersion ? setExportOpen(true) : handleGenerateClick('export')}
              disabled={generating || (!selectedVersion && !f)}
              className={`h-9 px-3 rounded-lg border text-[13px] font-medium whitespace-nowrap
                         flex items-center gap-1.5 transition-colors disabled:opacity-50
                         ${selectedVersion || canGenerate
                           ? 'border-stone text-midnight hover:border-plum/40 hover:text-plum'
                           : 'border-stone text-slate cursor-default'}`}
              title={selectedVersion ? 'Copy or download this version’s details' : 'Save a version and copy or download its details'}
            >
              <FileOutput size={14} />
              {generating && pendingAction === 'export' ? 'Saving...' : 'Export'}
            </button>
            {/* Open Online Quote — shown for saved versions with a portal token */}
            {selectedVersion?.portalUrl && activeOrg?.featureClientPortal !== false && (
              <a
                href={selectedVersion.portalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="h-9 px-3 rounded-lg border border-stone text-[13px] font-medium whitespace-nowrap
                           flex items-center gap-1.5 transition-colors
                           text-midnight hover:border-plum/40 hover:text-plum"
              >
                <ExternalLink size={14} />
                Open Online Quote
              </a>
            )}
            {/* When viewing a saved version with a PDF or online quotes enabled → "View Client Quote" */}
            {selectedVersion && (selectedVersion.pdfUrl || (activeOrg?.featureClientPortal !== false && selectedVersion.portalToken)) && (
              <button
                type="button"
                onClick={() => navigate(`${basePath}/client-quotes/${selectedVersion.id}`)}
                className="h-9 px-4 rounded-lg bg-plum text-white text-[13px] font-semibold whitespace-nowrap
                           flex items-center gap-1.5
                           hover:bg-rose active:opacity-80 transition-colors"
              >
                <Eye size={14} />
                View Client Quote
              </button>
            )}
            {/* When viewing unsaved (no selected version) → "Generate Client Quote" */}
            {!selectedVersion && (
              <button
                type="button"
                onClick={() => handleGenerateClick('pdf')}
                disabled={generating}
                className={`h-9 px-4 rounded-lg text-[13px] font-semibold whitespace-nowrap
                           transition-colors disabled:opacity-50
                           ${canGenerate
                             ? 'bg-plum text-white hover:bg-rose active:opacity-80'
                             : 'bg-slate/20 text-slate cursor-default'}`}
              >
                {generating && pendingAction === 'pdf' ? 'Generating...' : 'Generate Client Quote'}
              </button>
            )}
          </div>
        </div>

        {/* Missing details warning */}
        {showMissingWarning && !selectedVersion && missingDetails.length > 0 && (
          <div className="flex gap-2.5 p-3 bg-amber-50 border border-amber-200 rounded-xl">
            <AlertTriangle size={16} className="shrink-0 text-amber-600 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-medium text-amber-800 mb-1">
                Complete these details before generating a client quote:
              </p>
              <ul className="text-[12px] text-amber-700 leading-snug list-disc list-inside">
                {missingDetails.map(d => <li key={d}>{d}</li>)}
              </ul>
              <RateCardHint readiness={readiness} isOrgAdmin={isOrgAdmin} inline className="mt-1.5" />
            </div>
            <button
              type="button"
              onClick={() => setShowMissingWarning(false)}
              className="shrink-0 w-6 h-6 flex items-center justify-center rounded text-amber-500
                         hover:text-amber-700 transition-colors"
              aria-label="Dismiss"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* Old version banner */}
        {isViewingOldVersion && (
          <div className="flex gap-2.5 p-3 bg-amber-50 border border-amber-200 rounded-xl">
            <Info size={16} className="shrink-0 text-amber-600 mt-0.5" />
            <p className="text-[12px] text-amber-800 leading-snug">
              You are viewing an older version (v{selectedVersion!.versionNumber}).{' '}
              <button
                type="button"
                onClick={() => setSelectedVersionId(latestVersion!.id)}
                className="text-plum font-medium underline underline-offset-2 hover:text-rose"
              >
                View latest version (v{latestVersion!.versionNumber})
              </button>
            </p>
          </div>
        )}

        {/* No formula state */}
        {!f && !selectedVersion && (
          <div className="flex flex-col items-center py-12 gap-3 text-center">
            <p className="text-small font-medium text-midnight">No estimate available yet</p>
            <p className="text-[13px] text-slate leading-snug max-w-xs">
              Complete the quote details to trigger a formula calculation before generating a client quote.
            </p>
          </div>
        )}

        {/* Quote details cards */}
        {(f || selectedVersion) && (
          <>
            {/* Total + Tax Breakdown + Binding Quote */}
            <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
              {/* Main total */}
              <div className="px-4 py-4 flex items-center justify-between">
                <p className="font-semibold text-[15px] text-midnight">Total Quoted Cost</p>
                <p className="font-display text-[22px] font-bold text-plum tabular-nums">
                  {fmtCurrency(
                    displayData.totalWithTax ?? displayData.totalQuotedCost,
                    symbol,
                  )}
                </p>
              </div>

              {/* Binding Quote toggle */}
              <div className="px-4 py-3 border-t border-stone flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-midnight">Binding Quote</p>
                  <p className="text-[11px] text-slate leading-snug">
                    {quote.bindingQuote ? 'Price guaranteed to client.' : 'Non-binding estimate.'}
                  </p>
                </div>
                <button
                  type="button"
                  disabled={!!selectedVersion}
                  onClick={async () => {
                    const next = !quote.bindingQuote;
                    await updateQuote({ bindingQuote: next });
                  }}
                  className={`shrink-0 transition-colors ${selectedVersion ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title={selectedVersion ? 'Switch to "Latest (unsaved)" to change this setting' : undefined}
                >
                  {quote.bindingQuote
                    ? <ToggleRight size={32} className="text-plum" />
                    : <ToggleLeft size={32} className="text-slate" />}
                </button>
              </div>

              {/* Tax breakdown — shown when tax data exists (labor and materials rates split when they differ) */}
              {(displayData.laborTaxRate != null || displayData.requiresManualTaxReview) && (
                <div className="px-4 py-3 border-t border-stone">
                  <TaxBreakdown
                    subtotal={displayData.totalQuotedCost}
                    taxState={displayData.taxState}
                    laborTaxRate={displayData.laborTaxRate}
                    materialsTaxRate={displayData.materialsTaxRate}
                    materialsCost={displayData.materialsCost}
                    taxAmount={displayData.taxAmount}
                    totalWithTax={displayData.totalWithTax}
                    requiresManualTaxReview={displayData.requiresManualTaxReview}
                    symbol={symbol}
                  />
                </div>
              )}
            </div>

            {/* Quote Info */}
            <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-stone">
                <p className="font-semibold text-small text-midnight">Quote Information</p>
              </div>
              <div className="px-4">
                <DetailRow label="Quote ID" value={displayData.quoteVersionId} />
                <DetailRow
                  label="Quote Date"
                  value={displayData.quoteDate
                    ? formatMoveDate(displayData.quoteDate)
                    : undefined}
                />
                <DetailRow
                  label="Quote Expiration Date"
                  value={selectedVersion
                    ? formatExpiration(selectedVersion)
                    : expirationDate
                      ? formatMoveDate(expirationDate)
                      : 'Never expires'}
                />
                <DetailRow label="Customer" value={displayData.customerName} />
                <DetailRow label="Customer Email" value={displayData.customerEmail} />
                <DetailRow label="Origin Address" value={displayData.originAddress} />
                <DetailRow label="Destination Address" value={displayData.destinationAddress} />
                <DetailRow label="Billing Name" value={displayData.billingName} />
                <DetailRow label="Billing Address" value={[
                  displayData.billingAddressLine1,
                  displayData.billingAddressLine2,
                  [displayData.billingCity, displayData.billingState, displayData.billingZipCode].filter(Boolean).join(', '),
                ].filter(Boolean).join(', ') || undefined} />
              </div>
            </div>

            {/* Move Details */}
            <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-stone">
                <p className="font-semibold text-small text-midnight">Move Details</p>
              </div>
              <div className="px-4">
                {displayData.packingDate && (
                  <DetailRow
                    label="Packing Date"
                    value={formatMoveDate(displayData.packingDate)}
                  />
                )}
                <DetailRow
                  label="Preferred Move Date"
                  value={displayData.preferredMoveDate
                    ? formatMoveDate(displayData.preferredMoveDate)
                    : undefined}
                />
                <DetailRow
                  label="Move Deadline"
                  value={displayData.moveDeadline
                    ? formatMoveDate(displayData.moveDeadline)
                    : undefined}
                />
              </div>
            </div>

            {/* Line Items */}
            <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-stone flex items-center justify-between gap-3">
                <p className="font-semibold text-small text-midnight">Itemized Charges</p>
                {selectedVersion && (
                  <CopySectionButton packet={exportPacket} section="lineItems" onCopied={setCopiedKey} />
                )}
              </div>
              <div className="px-4">
                {displayData.lineItems.map((item, i) => (
                  <LineItemRow
                    key={i}
                    name={item.name}
                    rate={item.rate}
                    qty={item.qty}
                    total={item.total}
                    sym={symbol}
                    kind={item.kind}
                  />
                ))}
              </div>
            </div>

            {/* Additional Details — Crew & Trucks (saved versions), Materials & Scope of Services */}
            {(selectedVersion || displayData.showMaterials || displayData.showServices) && (
              <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-stone">
                  <p className="font-semibold text-small text-midnight">Additional Details</p>
                </div>
                <div className="px-4">
                  {selectedVersion && (
                    <div className="flex items-center justify-between gap-4 py-2.5 border-b border-stone last:border-0">
                      <div className="flex items-center gap-2">
                        <Users size={14} className="text-slate shrink-0" />
                        <p className="text-[13px] text-slate">Crew &amp; Trucks</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="text-[13px] font-medium text-midnight">
                          {selectedVersion.crewBreakdown
                            ? `${selectedVersion.crewBreakdown.crewSize} crew · ${selectedVersion.crewBreakdown.trucks} ${selectedVersion.crewBreakdown.trucks === 1 ? 'truck' : 'trucks'}`
                            : 'Not recorded'}
                        </p>
                        <CopySectionButton packet={exportPacket} section="crew" onCopied={setCopiedKey} />
                      </div>
                    </div>
                  )}
                  {displayData.showMaterials && (
                    <div className="flex items-center justify-between gap-4 py-2.5 border-b border-stone last:border-0">
                      <div className="flex items-center gap-2">
                        <Package size={14} className="text-slate shrink-0" />
                        <p className="text-[13px] text-slate">Materials</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="text-[13px] font-medium text-midnight">
                          {displayData.materialsCount} {displayData.materialsCount === 1 ? 'item' : 'items'}
                        </p>
                        {selectedVersion && (
                          <CopySectionButton packet={exportPacket} section="materials" onCopied={setCopiedKey} />
                        )}
                      </div>
                    </div>
                  )}
                  {displayData.showServices && (
                    <div className="flex items-center justify-between gap-4 py-2.5 border-b border-stone last:border-0">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 size={14} className="text-slate shrink-0" />
                        <p className="text-[13px] text-slate">Scope of Services</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="text-[13px] font-medium text-midnight">
                          {displayData.servicesCount} {displayData.servicesCount === 1 ? 'service' : 'services'}
                        </p>
                        {selectedVersion && (
                          <CopySectionButton packet={exportPacket} section="scope" onCopied={setCopiedKey} />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Push to SmartMoving — shown when integration is connected with push-back enabled and there's a version or formula */}
            {canPushToSM && (
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={handlePushToSmartMoving}
                  disabled={pushing}
                  className="w-full h-11 rounded-lg border border-sky-300 bg-sky-50 text-[13px] font-semibold text-sky-700
                             hover:bg-sky-100 active:bg-sky-150 disabled:opacity-50
                             transition-colors duration-150 flex items-center justify-center gap-2"
                >
                  {pushing ? <LoadingSpinner size="sm" /> : <Send size={14} />}
                  {pushing ? 'Pushing…' : 'Push to SmartMoving'}
                </button>
                {pushResult && (
                  <div className={`px-3 py-2 rounded-lg text-[13px] flex items-center gap-2
                    ${pushResult.success
                      ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                      : 'bg-rose/10 border border-rose/30 text-rose'}`}
                  >
                    {pushResult.success ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                    <span className="flex-1 min-w-0">{pushResult.message}</span>
                    <button
                      type="button"
                      onClick={() => setPushResult(null)}
                      className="shrink-0 opacity-60 hover:opacity-100"
                      aria-label="Dismiss"
                    >
                      <X size={12} />
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Expire previous versions confirmation */}
      {showExpireConfirm && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowExpireConfirm(false)} />
          <div className="relative w-full max-w-sm mx-4 mb-4 sm:mb-0 bg-white rounded-2xl shadow-brand-lg overflow-hidden">
            <div className="px-5 pt-5 pb-3">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={18} className="text-amber-500 shrink-0" />
                <p className="font-semibold text-[15px] text-midnight">Expire previous quotes?</p>
              </div>
              <p className="text-[13px] text-slate leading-snug">
                There are active quotes that haven&apos;t expired yet. Would you like to expire all
                previous versions before {pendingAction === 'export' ? 'saving a new one to export' : 'generating a new one'}?
              </p>
            </div>
            <div className="px-5 pb-5 flex flex-col gap-2">
              <button
                type="button"
                onClick={() => handleGenerate(true)}
                className="w-full h-10 rounded-lg bg-plum text-white text-[13px] font-semibold
                           hover:bg-rose active:opacity-80 transition-colors"
              >
                Expire Previous &amp; {pendingAction === 'export' ? 'Export' : 'Generate'}
              </button>
              <button
                type="button"
                onClick={() => handleGenerate(false)}
                className="w-full h-10 rounded-lg border border-stone text-[13px] font-medium text-midnight
                           hover:border-plum/40 hover:text-plum transition-colors"
              >
                Keep Previous &amp; {pendingAction === 'export' ? 'Export' : 'Generate'}
              </button>
              <button
                type="button"
                onClick={() => setShowExpireConfirm(false)}
                className="w-full h-10 rounded-lg text-[13px] text-slate hover:text-midnight transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export sheet — copy sections to the clipboard or download a spreadsheet */}
      {exportOpen && selectedVersion && (
        <ExportVersionSheet
          packet={exportPacket}
          loading={exportLoading}
          error={exportError}
          subtitle={`${selectedVersion.quoteVersionId} · ${selectedVersion.customerCompanyName}`}
          onClose={() => setExportOpen(false)}
          onCopied={setCopiedKey}
        />
      )}

      {copiedKey && (
        <SuccessToast
          message={`${EXPORT_COPY_LABELS[copiedKey]} copied. Paste into your proposal or invoicing tool.`}
          onDismiss={() => setCopiedKey(null)}
        />
      )}
    </div>
  );
}
